/**
 * Chat History Service
 * Service for fetching conversation history from Nexgo Concierge API
 * Following SOLID principles:
 * - Single Responsibility: Handles only conversation history API calls
 * - Open/Closed: Extensible through interfaces
 * - Liskov Substitution: Uses interface abstractions
 * - Interface Segregation: Focused, minimal interfaces
 * - Dependency Inversion: Depends on abstractions, not concrete implementations
 */

import axios, { AxiosError } from 'axios';
import {
  ConversationHistoryRequest,
  ConversationHistoryResponse,
  ConversationHistoryApiResponse,
  ConversationMessagesRequest,
  ConversationMessagesResponse,
  ConversationMessagesApiResponse,
  ConversationMessageItem,
} from '@/types/chatTypes';

// API Configuration
const CONVERSATIONS_API_BASE_URL = '';
const CONVERSATIONS_ENDPOINT = '/api/conversations';

// Create axios instance with default config
const conversationsApi = axios.create({
  baseURL: CONVERSATIONS_API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/**
 * Interface for conversation history service
 * Allows for dependency inversion and testing
 */
export interface IConversationHistoryService {
  fetchConversationHistory(
    request: ConversationHistoryRequest
  ): Promise<ConversationHistoryResponse>;
  fetchConversationMessages(
    request: ConversationMessagesRequest
  ): Promise<ConversationMessagesResponse>;
  fetchConversationById(
    conversationId: string
  ): Promise<ConversationMessagesResponse>;
  deleteConversation(userId: string, conversationId: string): Promise<void>;
}

/**
 * Validates conversation history request
 */
function validateConversationHistoryRequest(
  request: ConversationHistoryRequest
): string[] {
  const errors: string[] = [];

  if (!request.user_id || request.user_id.trim() === '') {
    errors.push('User ID is required');
  }

  return errors;
}

/**
 * Validates conversation messages request
 */
function validateConversationMessagesRequest(
  request: ConversationMessagesRequest
): string[] {
  const errors: string[] = [];

  if (!request.user_id || request.user_id.trim() === '') {
    errors.push('User ID is required');
  }

  if (!request.session_id || request.session_id.trim() === '') {
    errors.push('Session ID is required');
  }

  return errors;
}

/**
 * Default implementation of conversation history service
 */
class ConversationHistoryService implements IConversationHistoryService {
  /**
   * Fetches conversation history from the API
   * @param request - Conversation history request parameters
   * @returns Promise resolving to conversation history response
   * @throws Error if request is invalid or API call fails
   */
  async fetchConversationHistory(
    request: ConversationHistoryRequest
  ): Promise<ConversationHistoryResponse> {
    // Validate request
    const validationErrors = validateConversationHistoryRequest(request);
    if (validationErrors.length > 0) {
      throw new Error(`Invalid request: ${validationErrors.join(', ')}`);
    }

    try {
      // API format: /conversations/{user_id}
      const { user_id } = request;
      const url = `${CONVERSATIONS_ENDPOINT}/${user_id}`;

      console.log('📡 [ChatHistoryService] Fetching conversation history:', {
        url,
        user_id,
      });

      const response =
        await conversationsApi.get<ConversationHistoryApiResponse>(url);

      console.log('✅ [ChatHistoryService] Response received:', {
        status: response.status,
        hasData: !!response.data,
        success: response.data?.success,
        code: response.data?.code,
      });

      // Validate response structure
      if (!response.data) {
        throw new Error('Invalid response: No data received');
      }

      // Check if API returned an error
      if (!response.data.success || response.data.code !== 200) {
        throw new Error(
          response.data.message || 'Failed to fetch conversation history'
        );
      }

      // Extract conversations from the wrapped response
      if (!response.data.data || !response.data.data.conversations) {
        throw new Error('Invalid response: Missing conversations data');
      }

      // Return the unwrapped response format expected by the app
      return {
        conversations: response.data.data.conversations,
      };
    } catch (error) {
      // Enhanced error handling
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        // Handle different HTTP status codes
        switch (axiosError.response?.status) {
          case 400:
            throw new Error(
              'Invalid request. Please check your parameters and try again.'
            );
          case 401:
            throw new Error(
              'Authentication failed. Please log in and try again.'
            );
          case 403:
            throw new Error(
              'Access denied. You do not have permission to access this conversation.'
            );
          case 404:
            throw new Error(
              'Conversation not found. Please check your session ID and message.'
            );
          case 429:
            throw new Error(
              'Too many requests. Please wait a moment and try again.'
            );
          case 500:
            throw new Error('Server error. Please try again later.');
          case 503:
            throw new Error(
              'Service temporarily unavailable. Please try again later.'
            );
          default:
            const errorData = axiosError.response?.data as
              | { message?: string }
              | undefined;
            const errorMessage = errorData?.message || axiosError.message;
            throw new Error(
              `Conversation history service error: ${errorMessage}`
            );
        }
      }

      // Handle network errors
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          throw new Error(
            'Request timeout. Please check your connection and try again.'
          );
        }
        if (error.message.includes('Network Error')) {
          throw new Error(
            'Network error. Please check your internet connection.'
          );
        }
      }

      // Fallback error
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Fetches conversation messages from the API
   * @param request - Conversation messages request parameters
   * @returns Promise resolving to conversation messages response
   * @throws Error if request is invalid or API call fails
   */
  async fetchConversationMessages(
    request: ConversationMessagesRequest
  ): Promise<ConversationMessagesResponse> {
    // Validate request
    const validationErrors = validateConversationMessagesRequest(request);
    if (validationErrors.length > 0) {
      throw new Error(`Invalid request: ${validationErrors.join(', ')}`);
    }

    try {
      // API format: /conversations/messages/{user_id}/{session_id}
      const { user_id, session_id } = request;
      const url = `${CONVERSATIONS_ENDPOINT}/messages/${user_id}/${session_id}`;

      console.log('📡 [ChatHistoryService] Fetching conversation messages:', {
        url,
        user_id,
        session_id,
      });

      const response =
        await conversationsApi.get<ConversationMessagesApiResponse>(url);

      console.log('✅ [ChatHistoryService] Messages response received:', {
        status: response.status,
        hasData: !!response.data,
        success: response.data?.success,
        code: response.data?.code,
        messageCount: response.data?.data?.messages?.length || 0,
      });

      // Validate response structure
      if (!response.data) {
        throw new Error('Invalid response: No data received');
      }

      // Check if API returned an error
      if (!response.data.success || response.data.code !== 200) {
        throw new Error(
          response.data.message || 'Failed to fetch conversation messages'
        );
      }

      // Extract conversation data from the wrapped response
      if (!response.data.data || !response.data.data.messages) {
        throw new Error('Invalid response: Missing messages data');
      }

      // Flatten nested array structure: API returns messages as array of arrays (pairs)
      // Each inner array contains [userMessage, assistantMessage]
      // We need to flatten this to a single array of messages
      const rawMessages = response.data.data.messages;

      // Check if messages are nested (array of arrays)
      const isNested =
        Array.isArray(rawMessages) &&
        rawMessages.length > 0 &&
        Array.isArray(rawMessages[0]);

      const flattenedMessages = isNested
        ? rawMessages.flat() // Flatten nested array structure
        : rawMessages; // If already flat, use as is

      console.log('🔄 [ChatHistoryService] Processing messages:', {
        originalLength: rawMessages.length,
        flattenedLength: flattenedMessages.length,
        isNested,
        firstItemType: rawMessages.length > 0 ? typeof rawMessages[0] : 'empty',
        firstItemIsArray:
          rawMessages.length > 0 ? Array.isArray(rawMessages[0]) : false,
      });

      // Return the unwrapped response format expected by the app
      return {
        messages: flattenedMessages,
        session_id: response.data.data.conversationId,
        user_id: response.data.data.userId,
      };
    } catch (error) {
      // Enhanced error handling
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        // Handle different HTTP status codes
        switch (axiosError.response?.status) {
          case 400:
            throw new Error(
              'Invalid request. Please check your parameters and try again.'
            );
          case 401:
            throw new Error(
              'Authentication failed. Please log in and try again.'
            );
          case 403:
            throw new Error(
              'Access denied. You do not have permission to access this conversation.'
            );
          case 404:
            throw new Error(
              'Conversation not found. Please check your session ID and try again.'
            );
          case 429:
            throw new Error(
              'Too many requests. Please wait a moment and try again.'
            );
          case 500:
            throw new Error('Server error. Please try again later.');
          case 503:
            throw new Error(
              'Service temporarily unavailable. Please try again later.'
            );
          default:
            const errorData = axiosError.response?.data as
              | { message?: string }
              | undefined;
            const errorMessage = errorData?.message || axiosError.message;
            throw new Error(
              `Conversation messages service error: ${errorMessage}`
            );
        }
      }

      // Handle network errors
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          throw new Error(
            'Request timeout. Please check your connection and try again.'
          );
        }
        if (error.message.includes('Network Error')) {
          throw new Error(
            'Network error. Please check your internet connection.'
          );
        }
      }

      // Fallback error
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Fetches conversation messages by conversationId (for shared URLs)
   * @param conversationId - The conversation ID
   * @returns Promise resolving to conversation messages response
   * @throws Error if request is invalid or API call fails
   */
  async fetchConversationById(
    conversationId: string
  ): Promise<ConversationMessagesResponse> {
    if (!conversationId || conversationId.trim() === '') {
      throw new Error('Conversation ID is required');
    }

    try {
      // API format: /messages/{conversationId}?useRedis=true
      const url = `/api/messages/${conversationId}?useRedis=true`;

      console.log('📡 [ChatHistoryService] Fetching conversation by ID:', {
        url,
        conversationId,
      });

      const response =
        await conversationsApi.get<ConversationMessagesApiResponse>(url);

      console.log(
        '✅ [ChatHistoryService] Conversation by ID response received:',
        {
          status: response.status,
          hasData: !!response.data,
          success: response.data?.success,
          code: response.data?.code,
        }
      );

      // Validate response structure
      if (!response.data) {
        throw new Error('Invalid response: No data received');
      }

      // Check if API returned an error
      if (!response.data.success || response.data.code !== 200) {
        throw new Error(
          response.data.message || 'Failed to fetch conversation by ID'
        );
      }

      // Extract conversation data from the wrapped response
      // The API may return data.conversations array or data.messages
      const responseData = response.data.data as {
        messages?: ConversationMessageItem[];
        conversationId?: string;
        userId?: string;
        conversations?: Array<{
          id?: string;
          userId?: string;
          messages?: ConversationMessageItem[];
        }>;
      };

      // Check if we have messages directly
      if (responseData.messages && Array.isArray(responseData.messages)) {
        // Flatten nested array structure if needed
        const rawMessages = responseData.messages;
        const isNested =
          Array.isArray(rawMessages) &&
          rawMessages.length > 0 &&
          Array.isArray(rawMessages[0]);

        const flattenedMessages = isNested ? rawMessages.flat() : rawMessages;

        console.log(
          '🔄 [ChatHistoryService] Processing messages from conversationId:',
          {
            originalLength: rawMessages.length,
            flattenedLength: flattenedMessages.length,
            isNested,
          }
        );

        return {
          messages: flattenedMessages,
          session_id: responseData.conversationId || conversationId,
          user_id: responseData.userId || '',
        };
      }

      // If we have conversations array, extract messages from the first conversation
      if (
        responseData.conversations &&
        Array.isArray(responseData.conversations) &&
        responseData.conversations.length > 0
      ) {
        const conversation = responseData.conversations[0];
        if (conversation.messages && Array.isArray(conversation.messages)) {
          const rawMessages = conversation.messages;
          const isNested =
            Array.isArray(rawMessages) &&
            rawMessages.length > 0 &&
            Array.isArray(rawMessages[0]);

          const flattenedMessages = isNested ? rawMessages.flat() : rawMessages;

          return {
            messages: flattenedMessages,
            session_id: conversation.id || conversationId,
            user_id: conversation.userId || '',
          };
        }
      }

      throw new Error('Invalid response: Missing messages data');
    } catch (error) {
      // Enhanced error handling
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        // Extract error message from response
        const errorData = axiosError.response?.data as
          | { message?: string; success?: boolean }
          | undefined;
        const errorMessage = errorData?.message || '';

        // Handle different HTTP status codes
        switch (axiosError.response?.status) {
          case 400:
            // Check if it's a "not found" error
            if (
              errorMessage.toLowerCase().includes('not found') ||
              errorMessage.toLowerCase().includes('conversation not found')
            ) {
              throw new Error(
                'Conversation not found. The shared conversation may have been deleted or the link is invalid.'
              );
            }
            throw new Error(
              errorMessage ||
                'Invalid request. Please check your conversation ID and try again.'
            );
          case 401:
            throw new Error(
              'Authentication failed. Please log in and try again.'
            );
          case 403:
            throw new Error(
              'Access denied. You do not have permission to access this conversation.'
            );
          case 404:
            throw new Error(
              'Conversation not found. Please check your conversation ID and try again.'
            );
          case 429:
            throw new Error(
              'Too many requests. Please wait a moment and try again.'
            );
          case 500:
            throw new Error('Server error. Please try again later.');
          case 503:
            throw new Error(
              'Service temporarily unavailable. Please try again later.'
            );
          default:
            // Use the errorMessage extracted above, or fallback to axios error message
            const defaultErrorMessage = errorMessage || axiosError.message;
            throw new Error(
              `Conversation by ID service error: ${defaultErrorMessage}`
            );
        }
      }

      // Handle network errors
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          throw new Error(
            'Request timeout. Please check your connection and try again.'
          );
        }
        if (error.message.includes('Network Error')) {
          throw new Error(
            'Network error. Please check your internet connection.'
          );
        }
      }

      // Fallback error
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Deletes a conversation from the API
   * @param userId - The user ID
   * @param conversationId - The conversation ID to delete
   * @returns Promise resolving when deletion is complete
   * @throws Error if request is invalid or API call fails
   */
  async deleteConversation(
    userId: string,
    conversationId: string
  ): Promise<void> {
    if (!userId || userId.trim() === '') {
      throw new Error('User ID is required');
    }

    if (!conversationId || conversationId.trim() === '') {
      throw new Error('Conversation ID is required');
    }

    try {
      // API format: /conversations/delete/{user_id}/{conversation_id}
      const url = `/api/conversations/delete/${userId}/${conversationId}`;

      console.log('📡 [ChatHistoryService] Deleting conversation:', {
        url,
        userId,
        conversationId,
      });

      const response = await conversationsApi.delete(url);

      console.log('✅ [ChatHistoryService] Delete response received:', {
        status: response.status,
      });

      // Check if deletion was successful (204 No Content or 200 OK)
      if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to delete conversation');
      }
    } catch (error) {
      // Enhanced error handling
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        // Handle different HTTP status codes
        switch (axiosError.response?.status) {
          case 400:
            throw new Error(
              'Invalid request. Please check your parameters and try again.'
            );
          case 401:
            throw new Error(
              'Authentication failed. Please log in and try again.'
            );
          case 403:
            throw new Error(
              'Access denied. You do not have permission to delete this conversation.'
            );
          case 404:
            throw new Error(
              'Conversation not found. It may have already been deleted.'
            );
          case 429:
            throw new Error(
              'Too many requests. Please wait a moment and try again.'
            );
          case 500:
            throw new Error('Server error. Please try again later.');
          case 503:
            throw new Error(
              'Service temporarily unavailable. Please try again later.'
            );
          default:
            const errorData = axiosError.response?.data as
              | { message?: string }
              | undefined;
            const errorMessage = errorData?.message || axiosError.message;
            throw new Error(
              `Delete conversation service error: ${errorMessage}`
            );
        }
      }

      // Handle network errors
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          throw new Error(
            'Request timeout. Please check your connection and try again.'
          );
        }
        if (error.message.includes('Network Error')) {
          throw new Error(
            'Network error. Please check your internet connection.'
          );
        }
      }

      // Fallback error
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }
}

// Export singleton instance
export const conversationHistoryService: IConversationHistoryService =
  new ConversationHistoryService();

/**
 * Convenience function to fetch conversation history
 * @param user_id - The user ID
 * @returns Promise resolving to conversation history response
 */
export async function fetchConversationHistory(
  user_id: string
): Promise<ConversationHistoryResponse> {
  return conversationHistoryService.fetchConversationHistory({
    user_id,
  });
}

/**
 * Convenience function to fetch conversation messages
 * @param user_id - The user ID
 * @param session_id - The session ID (conversation ID)
 * @returns Promise resolving to conversation messages response
 */
export async function fetchConversationMessages(
  user_id: string,
  session_id: string
): Promise<ConversationMessagesResponse> {
  return conversationHistoryService.fetchConversationMessages({
    user_id,
    session_id,
  });
}

/**
 * Convenience function to fetch conversation by ID (for shared URLs)
 * @param conversationId - The conversation ID
 * @returns Promise resolving to conversation messages response
 */
export async function fetchConversationById(
  conversationId: string
): Promise<ConversationMessagesResponse> {
  return conversationHistoryService.fetchConversationById(conversationId);
}

/**
 * Convenience function to delete a conversation
 * @param userId - The user ID
 * @param conversationId - The conversation ID to delete
 * @returns Promise resolving when deletion is complete
 */
export async function deleteConversation(
  userId: string,
  conversationId: string
): Promise<void> {
  return conversationHistoryService.deleteConversation(userId, conversationId);
}
